// src/pages/QRKioskPage.js
// Mobile-optimised single-vendor kiosk opened via QR code
// No vendor login required — public page
// URL: /order/vendor/:slug  or  /order/vendor/:slug?table=3

import React, { useState, useEffect } from 'react';
// No router needed - slug read from window.location.pathname
import { useCart }     from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import api             from '../services/api';
import PaymentModal    from '../components/PaymentModal';

// Use existing api instance for public calls
// Menu and public vendor endpoints don't require auth
const publicApi = api;
import ModifierSelector from '../components/ModifierSelector';
import CashGateModal   from '../components/CashGateModal';
import LangToggle      from '../components/LangToggle';
import { applyTheme }  from '../utils/applyTheme';
import { calculateServiceFee } from '../utils/serviceFeeCalculator';
import { applyDietary, categoriesOf } from '../utils/dietary';
import DietarySheet    from '../components/DietarySheet';
import { DietaryPill, DietaryHiddenNote, UnverifiedTag } from '../components/DietaryFilterUI';

export default function QRKioskPage() {
  // Read slug from URL path: /order/vendor/:slug
  const slug = window.location.pathname.split('/order/vendor/')[1]?.split('?')[0]?.split('/')[0] || null;
  const { t, isAr, itemName } = useLanguage();
  const { cart, subtotal, tax, total, addItem, setQty, removeItem, clearCart } = useCart();

  const [vendor,       setVendor]       = useState(null);
  const [menu,         setMenu]         = useState([]);
  const [, setCategories] = useState([]);
  const [cat,          setCat]          = useState('All');
  const [diet,         setDiet]         = useState('none');   // session-only dietary preference
  const [showDietSheet, setShowDietSheet] = useState(true);
  const [showUnverified, setShowUnverified] = useState(false);
  const [search,       setSearch]       = useState('');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [tableNo,      setTableNo]      = useState(null);
  const [showPayment,  setShowPayment]  = useState(false);
  const [showCashGate, setShowCashGate] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [ordered,      setOrdered]      = useState(false);
  const [orderedCashPending, setOrderedCashPending] = useState(false);
  const [orderNo,      setOrderNo]      = useState('');
  const [modifierItem, setModifierItem] = useState(null);
  const [cartOpen,     setCartOpen]     = useState(false);

  // Read table from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tbl    = params.get('table');
    if (tbl) setTableNo(tbl);
  }, []);

  // Load vendor + menu
  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        // Load vendor info (public endpoint — no auth needed)
        let vendorData;
        try {
          const vRes = await publicApi.get(`/public/vendor/${slug}`);
          vendorData = vRes.data;
          setVendor(vRes.data);
          applyTheme(vendorData.kioskTheme || 'dark', vendorData.brandColor, vendorData.bgColor, vendorData.tileColor, vendorData.fontColor, vendorData.buttonTextColor, vendorData.surfaceColor);
        } catch (vErr) {
          const msg = vErr.response?.data?.error || vErr.message;
          // If 404 - vendor slug not set
          if (vErr.response?.status === 404) {
            setError('Stall not found. Check the QR code or ask staff for help.');
          } else {
            setError('Could not reach the server. Please check your connection.');
          }
          return;
        }

        // Load menu
        try {
          const mRes = await publicApi.get(`/menu?vendorId=${vendorData.id}&active=true`);
          const items = mRes.data || [];
          setMenu(items);
          const cats = ['All', ...new Set(items.map(i => i.category).filter(Boolean))];
          setCategories(cats);
          if (items.length === 0) {
            setError('No menu items available right now.');
          }
        } catch (mErr) {
          setError('Could not load menu. Please try again.');
        }
      } catch (err) {
        setError(err.message || 'Could not load menu');
      } finally { setLoading(false); }
    };
    load();
    return () => applyTheme('dark');
  }, [slug]);

  const { shown: dietItems, unverified: dietUnverified } = applyDietary(menu, diet, showUnverified);
  const visibleCats = categoriesOf(dietItems);
  const activeCat   = visibleCats.includes(cat) ? cat : 'All';

  const filtered = dietItems
    .filter(i => activeCat === 'All' || i.category === activeCat)
    .filter(i => {
      const q = search.toLowerCase();
      return i.name?.toLowerCase().includes(q) || i.nameAr?.includes(search);
    });

  const getQty = (id) => cart.find(i => i.id === id)?.qty || 0;

  const handleItemTap = async (item) => {
    try {
      const r = await api.get(`/menu/${item.id}/modifiers`);
      if (r.data?.length > 0) { setModifierItem(item); return; }
    } catch (_) {}
    addItem(item);
  };

  const handleProceedToPayment = async () => {
    if (!cart.length) return;
    try {
      const res = await api.post('/orders', {
        vendorId:    vendor.id,
        items:       cart.map(i => ({ menuItemId: i.id, quantity: i.qty })),
        paymentMode: 'CARD',
        tableNo:     tableNo || undefined,
        sourceKiosk: 'STALL_QR',
        notes:       cart.filter(i => i.modifierNote).map(i => `${i.name}: ${i.modifierNote}`).join(' | '),
      });
      setPendingOrder(res.data);
      setShowPayment(true);
    } catch (err) {
      // Fallback demo order
      setPendingOrder({
        id:            'demo-' + Date.now(),
        orderNumber:   'QR-' + Date.now().toString(36).toUpperCase(),
        total,
        subtotal,
        tax,
        items:         cart.map(i => ({ name: i.name, quantity: i.qty, price: i.price })),
        paymentStatus: 'PENDING',
        paymentMode:   'CARD',
        tableNo,
        sourceKiosk:   'STALL_QR',
      });
      setShowPayment(true);
    }
  };

  const handlePaymentSuccess = ({ cashPending } = {}) => {
    setShowPayment(false);
    setOrderNo(pendingOrder?.orderNumber || 'QR-' + Date.now().toString(36).toUpperCase());
    setOrdered(true);
    setOrderedCashPending(!!cashPending);
    clearCart();
  };

  // ── Ordered confirmation screen ───────────────────────────
  if (ordered) return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 24, textAlign: 'center',
    }}>
      <div style={{ fontSize: 64, marginBottom: 16 }}>{orderedCashPending ? '🎫' : '✅'}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: orderedCashPending ? 'var(--accent)' : 'var(--green)', marginBottom: 8 }}>
        {orderedCashPending ? 'Pay at the Counter' : 'Order Placed!'}
      </div>
      <div style={{
        background: 'var(--bg2)', border: '2px dashed var(--accent)',
        borderRadius: 14, padding: '16px 32px', margin: '16px 0',
      }}>
        <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
          Your Order Number
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--accent)', letterSpacing: 2 }}>
          {orderNo}
        </div>
        {tableNo && (
          <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8 }}>
            Table {tableNo}
          </div>
        )}
      </div>
      <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 24, lineHeight: 1.7 }}>
        {vendor?.emoji} {vendor?.name}<br/>
        {orderedCashPending ? 'Pay cash at the counter. Preparation starts once payment is received.' : 'Your order is being prepared.'}
      </div>
      <button className="btn btn-ghost" onClick={() => { setOrdered(false); setPendingOrder(null); setOrderedCashPending(false); }}>
        Order More →
      </button>
    </div>
  );

  // ── Loading / error ───────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: 'var(--text2)', fontSize: 14 }}>Loading menu...</div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
      <div>
        <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--red)', marginBottom: 8 }}>Stall Unavailable</div>
        <div style={{ fontSize: 14, color: 'var(--text2)' }}>{error}</div>
      </div>
    </div>
  );

  const feeConfig  = vendor?.serviceFeeConfig
    ? (typeof vendor.serviceFeeConfig === 'string' ? (() => { try { return JSON.parse(vendor.serviceFeeConfig); } catch(_) { return null; } })() : vendor.serviceFeeConfig)
    : null;
  const serviceFee = calculateServiceFee(subtotal, feeConfig);
  const cartTotal  = parseFloat((total + serviceFee.amount).toFixed(2));
  const cartCount  = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', paddingBottom: cartCount > 0 ? 120 : 20 }}>

      {/* Header */}
      <div style={{
        background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
        padding: '14px 16px', position: 'sticky', top: 0, zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{vendor?.emoji} {vendor?.name}</div>
            {tableNo && (
              <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 2 }}>
                🪑 Table {tableNo}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LangToggle />
            <div style={{
              fontSize: 10, padding: '3px 8px', borderRadius: 10,
              background: 'var(--bg3)', color: 'var(--text3)',
            }}>
              📱 QR Order
            </div>
          </div>
        </div>
        {/* Search */}
        <input
          type="text" value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search menu..."
          style={{ width: '100%', marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
        />
      </div>

      {diet !== 'none' && (
        <div style={{ padding: '10px 16px 0' }}>
          <DietaryPill preference={diet} isAr={isAr} onClear={() => { setDiet('none'); setShowUnverified(false); }} />
        </div>
      )}

      {/* Category tabs */}
      <div style={{
        display: 'flex', gap: 8, padding: '12px 16px',
        overflowX: 'auto', background: 'var(--bg2)',
        borderBottom: '1px solid var(--border)',
      }}>
        {visibleCats.map(c => (
          <button key={c} onClick={() => setCat(c)} style={{
            padding: '6px 14px', borderRadius: 20, fontSize: 12,
            whiteSpace: 'nowrap', cursor: 'pointer', border: 'none',
            background: activeCat === c ? 'var(--accent)' : 'var(--bg3)',
            color: activeCat === c ? '#000' : 'var(--text2)',
            fontWeight: activeCat === c ? 700 : 400,
          }}>
            {c}
          </button>
        ))}
      </div>

      {/* Menu items — mobile card list */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.map(item => {
          const qty = getQty(item.id);
          return (
            <div key={item.id} style={{
              background: 'var(--bg2)', borderRadius: 12,
              border: `1px solid ${qty > 0 ? 'var(--accent)' : 'var(--border)'}`,
              display: 'flex', alignItems: 'center', gap: 12, padding: 12,
            }}>
              {/* Item image or emoji */}
              {item.imageUrl ? (
                <img src={item.imageUrl} alt={item.name} style={{ width: 64, height: 64, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} onError={e => e.target.style.display='none'} />
              ) : (
                <div style={{ width: 64, height: 64, borderRadius: 8, background: 'var(--bg3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32, flexShrink: 0 }}>
                  {item.emoji}
                </div>
              )}

              {/* Info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{itemName(item)}{item.unverified && <UnverifiedTag isAr={isAr} />}</div>
                {item.description && (
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.description}
                  </div>
                )}
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--accent)', marginTop: 4 }}>
                  AED {item.price}
                </div>
              </div>

              {/* Qty controls */}
              <div style={{ flexShrink: 0 }}>
                {qty > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => setQty(item.id, qty - 1)} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--bg4)', border: '1px solid var(--border)', color: 'var(--text)', fontSize: 16, cursor: 'pointer' }}>−</button>
                    <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{qty}</span>
                    <button onClick={() => handleItemTap(item)} style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--accent)', border: 'none', color: 'var(--button-text)', fontSize: 16, cursor: 'pointer' }}>+</button>
                  </div>
                ) : (
                  <button onClick={() => handleItemTap(item)} style={{ padding: '8px 14px', borderRadius: 8, background: 'var(--accent)', border: 'none', color: 'var(--button-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                    {item.modifierGroups?.length > 0 ? 'Choose' : 'Add'}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
            No items found
          </div>
        )}
        {diet !== 'none' && (
          <DietaryHiddenNote count={dietUnverified.length} expanded={showUnverified} isAr={isAr} onToggle={() => setShowUnverified(v => !v)} />
        )}
      </div>

      {showDietSheet && (
        <DietarySheet isAr={isAr} initial={diet}
          onContinue={(p) => { setDiet(p || 'none'); setShowDietSheet(false); }}
          onSkip={() => setShowDietSheet(false)} />
      )}

      {/* Sticky cart bar */}
      {cartCount > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          background: 'var(--bg2)', borderTop: '1px solid var(--border)',
          padding: '12px 16px', zIndex: 200,
        }}>
          {/* Cart items preview */}
          <div style={{ marginBottom: 10, maxHeight: cartOpen ? 200 : 0, overflow: 'hidden', transition: 'max-height .3s' }}>
            {cart.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text2)' }}>{item.emoji} {item.name} ×{item.qty}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'var(--accent)' }}>AED {(item.price * item.qty).toFixed(2)}</span>
                  <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: 'var(--text3)', cursor: 'pointer' }}>✕</button>
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 12, color: 'var(--text2)' }}>
              <span>Subtotal</span><span>AED {subtotal.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12, color: 'var(--text2)' }}>
              <span>VAT (5%)</span><span>AED {tax.toFixed(2)}</span>
            </div>
            {serviceFee.amount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 12, color: 'var(--text2)' }}>
                <span>{serviceFee.label}</span><span>AED {serviceFee.amount.toFixed(2)}</span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button onClick={() => setCartOpen(p => !p)} style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', cursor: 'pointer', color: 'var(--text2)', fontSize: 12 }}>
              {cartOpen ? '▼' : '▲'} {cartCount} item{cartCount > 1 ? 's' : ''}
            </button>
            <button className="btn btn-primary" style={{ flex: 1, padding: 12, fontSize: 15, fontWeight: 700 }}
              onClick={handleProceedToPayment}>
              Pay AED {cartTotal.toFixed(2)} →
            </button>
          </div>
        </div>
      )}

      {/* Modifier selector */}
      {modifierItem && (
        <ModifierSelector
          item={modifierItem}
          onAdd={item => { addItem(item); setModifierItem(null); }}
          onClose={() => setModifierItem(null)}
        />
      )}

      {/* Payment modal */}
      {showPayment && pendingOrder && (
        <PaymentModal
          order={pendingOrder}
          vendorId={vendor?.id}
          paymentConfig={vendor?.paymentConfig}
          sourceKiosk="STALL_QR"
          onSuccess={handlePaymentSuccess}
          onClose={() => { setShowPayment(false); setPendingOrder(null); }}
        />
      )}

      {/* Cash gate modal */}
      {showCashGate && (
        <CashGateModal
          orderTotal={cartTotal}
          onConfirm={() => { setShowCashGate(false); handlePaymentSuccess(); }}
          onCancel={() => setShowCashGate(false)}
        />
      )}
    </div>
  );
}
